import type { SortOption, SortState } from '@/lib/filters/types'
import type { RelatedOrgSort } from '@/gql/graphql'
import type { ClientTableRow } from '@/components/clients/ClientsTable'
import { getPlaceholderNextAction } from './placeholder-data'

/**
 * The Client Listing's SERVER sort (FE-REQUIREMENT §5, ADR-018): a `{key, order}` sort state (the list page's existing `sort` / `order` URL params) mapped
 * onto the server's `RelatedOrgSort` vocabulary, as a WHITELIST.
 *
 * The server vocabulary is the GENERATED `RelatedOrgSort` (`src/gql`, regenerated from the deployed dev gateway: `ENTITY_COUNT_DESC`, `NAME_ASC`,
 * `NAME_DESC`), no longer a hand-written union. `OPTION_FOR_SERVER` below is a `satisfies Record<RelatedOrgSort, ...>`, so if the BE ever adds, renames or
 * drops a value the BUILD fails (a missing key, or an unknown one) instead of this file quietly disagreeing with the server; `client-sort.test.ts` holds the
 * runtime half against the generated schema.
 *
 * Only three orderings exist server-side, deliberately: "needs attention first" and "earliest renewal" were left OUT of the enum by human ruling, so the
 * server rejects them loudly instead of accepting them and returning an unsorted list. Do not offer either. And ADR-018 forbids a client-side re-sort over a
 * paginated result, so "Client name Z-A" is NOT free: it goes to the server as `NAME_DESC` or it does not exist.
 */
export type ClientServerSort = RelatedOrgSort

/**
 * Compile-time exhaustiveness against the GENERATED enum: every server ordering has exactly one option id here. Adding, renaming or removing a
 * `RelatedOrgSort` member on the server makes this fail to type-check (missing / excess key), which is the whole point of this record.
 */
export const OPTION_FOR_SERVER = {
  NAME_ASC: 'name-asc',
  NAME_DESC: 'name-desc',
  ENTITY_COUNT_DESC: 'entities-desc',
} as const satisfies Record<RelatedOrgSort, string>

export interface ClientSortOption {
  /** Stable id (the Select's value). */
  id: string
  label: string
  /** The list-state `{key, order}` this option corresponds to (the existing `sort` / `order` URL params). */
  state: SortState
  server: ClientServerSort
}

/** The three orderings the server can do, in the order they are offered. Labels are ASSUMED, not ruled: the owner has not signed them off (EW-FIX-12b). */
export const CLIENT_SORT_OPTIONS: readonly ClientSortOption[] = [
  { id: 'name-asc', label: 'Client name A–Z', state: { key: 'name', order: 'asc' }, server: 'NAME_ASC' },
  { id: 'name-desc', label: 'Client name Z–A', state: { key: 'name', order: 'desc' }, server: 'NAME_DESC' },
  { id: 'entities-desc', label: 'Most entities', state: { key: 'entityCount', order: 'desc' }, server: 'ENTITY_COUNT_DESC' },
]

/**
 * The default ordering. It is the server's own inherent order (`ORDER BY t.name ASC, t.id ASC`, measured before `sort` existed), so choosing it changes
 * nothing about what the server does, but it REPLACES the page's current default, a page-LOCAL Jurisdiction-ascending re-sort (`ClientsPage.config.ts`
 * `sort.default = {country, asc}`), which cannot exist on the server (there is no country ordering). That is a visible change to what an agency sees
 * first and is an OWNER DECISION, recorded in EW-FIX-12b.
 */
export const DEFAULT_CLIENT_SORT: ClientSortOption = CLIENT_SORT_OPTIONS[0]

/** The option whose `{key, order}` is EXACTLY this state, or `undefined`. Exact match on both fields, and it never guesses. */
export function clientSortOptionFor(state: SortState | null | undefined): ClientSortOption | undefined {
  if (!state) return undefined
  return CLIENT_SORT_OPTIONS.find((o) => o.state.key === state.key && o.state.order === state.order)
}

/**
 * Does this state have NO server equivalent? True for a sortable column the server cannot order by (Jurisdiction, Entities ascending) and for any
 * value that came from a hand-edited URL. Those remain page-local orderings (the ADR-018 known exception) and must be labelled as such; `null` (no sort
 * in the URL) is not page-local, it is the default.
 */
export function isPageLocalSort(state: SortState | null | undefined): boolean {
  return !!state && clientSortOptionFor(state) === undefined
}

/**
 * The `sort` argument to SEND on the request. ALWAYS one of the three server values, never anything derived from the URL: a state with no server
 * equivalent (a page-local column, a hand-edited `sort=bogus`, `ENTITY_COUNT_ASC`, `__proto__`) sends the DEFAULT explicitly, so the server never receives
 * a string it does not know, and never relies on what "sort absent" means on its side.
 */
export function serverSortFor(state: SortState | null | undefined): ClientServerSort {
  return (clientSortOptionFor(state) ?? DEFAULT_CLIENT_SORT).server
}

/** The option a control should SHOW as selected: the state's own, or the default for `null`; `undefined` when the state is page-local (nothing is selected). */
export function selectedClientSortOption(state: SortState | null | undefined): ClientSortOption | undefined {
  return state ? clientSortOptionFor(state) : DEFAULT_CLIENT_SORT
}

/**
 * The ONE Clients sort option list, shared by both the agency and SA `ListPageConfig`s (EW-T
 * sidebar-filter-rollout) — rendered through the canonical `SortControl` (toggle variant: field
 * picker + a separate asc/desc icon), replacing the page's old bespoke flat-list `ClientSortSelect`.
 *
 * `name` / `entityCount` map to a real server ordering for the AGENCY branch only (`client-sort.ts`'s
 * `isPageLocalSort`/`serverSortFor`, `relatedOrgsConnection(sort: RelatedOrgSort)`); `country` never
 * does. The SA branch's `relatedOrgs` query has no `sort` argument at all (fixed `name ASC` server
 * order — see `useRelatedOrgs`'s own doc comment) — EVERY option on that branch is page-local.
 * `attention`/`renewal` are page-local on both branches: they reorder by `clientLead`/
 * `agreementEndDate`/a placeholder next-action, none of which any server sort argument covers.
 */
export const CLIENT_SORT_FIELD_OPTIONS: SortOption[] = [
  { key: 'name', label: 'Client name' },
  { key: 'country', label: 'Jurisdiction' },
  { key: 'entityCount', label: 'Entities' },
  { key: 'attention', label: 'Needs attention first' },
  { key: 'renewal', label: 'Earliest renewal' },
]

/**
 * Re-sorts `rows` CLIENT-SIDE for a sort with no server equivalent (ADR-018 known exception) —
 * `attention`/`renewal` (never server-backed, either branch) or any other `CLIENT_SORT_FIELD_OPTIONS`
 * key on a branch whose query cannot order by it. Callers gate this behind their own "is this
 * page-local for MY branch" check (`isPageLocalSort` for agency; SA treats every option but the
 * `{name, asc}` default as page-local, since its query has no sort argument at all) — this function
 * itself does not know which branch called it.
 */
export function sortClientRowsLocally<T extends ClientTableRow>(rows: T[], sort: SortState | null | undefined): T[] {
  if (!sort) return rows
  const { key, order } = sort
  const dir = order === 'asc' ? 1 : -1
  if (key === 'attention') {
    // Unassigned lead first, then the soonest next step — the same two facts the Needs-attention view is built on.
    const k = (r: T) => [r.clientLead == null ? 0 : 1, new Date(getPlaceholderNextAction(r.tenantId).dueDate).getTime()] as const
    return [...rows].sort((a, b) => dir * (k(a)[0] - k(b)[0] || k(a)[1] - k(b)[1]))
  }
  if (key === 'renewal') {
    const t = (r: T) => (r.agreementEndDate ? new Date(r.agreementEndDate).getTime() : Number.POSITIVE_INFINITY)
    return [...rows].sort((a, b) => dir * (t(a) - t(b)))
  }
  return [...rows].sort((a, b) => {
    const av = a[key as keyof ClientTableRow]
    const bv = b[key as keyof ClientTableRow]
    if (av == null && bv == null) return 0
    if (av == null) return 1
    if (bv == null) return -1
    return av < bv ? -dir : av > bv ? dir : 0
  })
}
